// @ts-strict-ignore
import { createReducer, on } from '@ngrx/store';
import {
  createEntityAdapter,
  EntityAdapter,
  EntityState,
  Update,
} from '@ngrx/entity';
import * as CoreVehicleActions from '../actions/vehicle.actions';
import * as VehicleActions from '../../vehicles/store/actions';
import { Vehicle, VehicleStateType, VehicleType } from '@fc-core';
import { union } from 'lodash-es';
import { addVehiclesToGroup, removeVehicleFromGroupSuccess } from '../actions';

export const vehiclesFeatureKey = 'vehicles';

export interface State extends EntityState<Vehicle> {
  loading: boolean;
  loaded: boolean;
  fields: string[];
  count: number;
  vehicleTypes: VehicleType[];
  usedVehicleTypes: VehicleType[];
  selectedVehicleId: number;
  vehicleError: any;
  setLocationLoading: boolean;
}

export const adapter: EntityAdapter<Vehicle> = createEntityAdapter<Vehicle>();

export const initialState: State = adapter.getInitialState({
  loading: false,
  loaded: false,
  fields: [],
  count: 0,
  vehicleTypes: null,
  usedVehicleTypes: [],
  selectedVehicleId: null,
  vehicleError: null,
  setLocationLoading: false,
});

export const reducer = createReducer(
  initialState,
  on(
    CoreVehicleActions.addVehicle,
    CoreVehicleActions.updateVehicle,
    (state) => ({
      ...state,
      loading: true,
    }),
  ),
  on(CoreVehicleActions.addVehicleSuccess, (state, action) => ({
    ...adapter.addOne(action.vehicle, state),
    count: state.count + 1,
    loading: false,
  })),
  on(CoreVehicleActions.updateVehicleSuccess, (state, action) => ({
    ...adapter.updateOne(action.vehicle, state),
    loading: false,
  })),
  on(CoreVehicleActions.setLoaded, (state) => ({
    ...state,
    loaded: true,
  })),
  on(CoreVehicleActions.deleteVehicleSuccess, (state, action) => ({
    ...adapter.removeOne(action.id, state),
    count: state.count - 1,
  })),
  on(CoreVehicleActions.loadVehicles, (state) => {
    const vehicles = adapter.getSelectors().selectAll(state);
    const updatedState = vehicles.length
      ? adapter.updateMany(
          vehicles.map((vehicle) => ({
            id: vehicle.id,
            changes: { ...vehicle, loadingFields: true },
          })),
          state,
        )
      : state;
    return {
      ...updatedState,
      loading: true,
      loaded: false,
    };
  }),

  on(addVehiclesToGroup, (state, action) => {
    const updates: Update<Vehicle>[] = action.vehicleIdList.map((vehicleId) => {
      const vehicle = state.entities[vehicleId];
      return {
        id: vehicleId,
        changes: {
          ...vehicle,
          groups: union(vehicle.groups, [action.groupId]),
        },
      };
    });

    return adapter.updateMany(updates, state);
  }),

  on(removeVehicleFromGroupSuccess, (state, action) => {
    const vehicle = adapter.getSelectors().selectEntities(state)[
      action.vehicleId
    ];
    return {
      ...adapter.updateOne(
        {
          id: action.vehicleId,
          changes: {
            ...vehicle,
            groups: vehicle.groups.filter((g) => g !== action.vehicleGroup.id),
          },
        },
        state,
      ),
    };
  }),
  on(CoreVehicleActions.cancelLoadVehicles, (state) => ({
    ...adapter.updateMany(
      adapter
        .getSelectors()
        .selectAll(state)
        .map((vehicle) => ({
          id: vehicle.id,
          changes: { ...vehicle, loadingFields: false },
        })),
      state,
    ),
    loading: false,
  })),
  on(CoreVehicleActions.reloadVehicles, (state) => ({
    ...adapter.removeAll({ ...state }),
    loading: false,
    loaded: false,
    count: 0,
    fields: [] as string[],
  })),
  on(CoreVehicleActions.loadVehiclesSuccess, (state, action) => ({
    ...adapter.addMany(
      action.vehicles.map((vehicle) => ({ ...vehicle, loadingFields: false })),
      state,
    ),
    loading: false,
    fields: union(state.fields, action.fields),
    count: action.count ? action.count : state.count,
  })),
  on(CoreVehicleActions.selectVehicleId, (state, action) => ({
    ...state,
    selectedVehicleId: +action.vehicleId,
  })),
  on(CoreVehicleActions.updateVehicles, (state, action) => ({
    ...adapter.updateMany(
      action.vehicles.map((vehicle) => ({
        id: vehicle.id,
        changes: { ...vehicle, loadingFields: false },
      })),
      state,
    ),
    fields: union(state.fields, action.fields),
    count: action.count ? action.count : state.count,
    loading: false,
  })),
  on(CoreVehicleActions.eventSourceVehicleUpdates, (state, action) =>
    adapter.updateMany(action.vehicles, state),
  ),
  on(CoreVehicleActions.loadVehicleTypesSuccess, (state, action) => {
    return {
      ...state,
      vehicleTypes: action.vehicleTypes,
    };
  }),
  on(CoreVehicleActions.loadUsedVehicleTypesSuccess, (state, action) => {
    return {
      ...state,
      usedVehicleTypes: action.vehicleTypes,
    };
  }),
  on(CoreVehicleActions.vehiclesError, (state, action) => {
    return {
      ...state,
      loading: false,
      vehicleError: action.error,
    };
  }),
  on(CoreVehicleActions.updateVehicleMainPhoto, (state, action) => {
    const payload: Vehicle = {
      ...adapter.getSelectors().selectEntities(state)[action.vehicleId],
      photo: action.photo,
    };
    return {
      ...adapter.updateOne(
        {
          id: action.vehicleId,
          changes: payload,
        },
        state,
      ),
    };
  }),
  on(CoreVehicleActions.setVehicleLocationSuccess, (state, action) => {
    const entities = adapter.getSelectors().selectEntities(state);
    const vehicle = entities[action.vehicleId];

    const update = {
      id: action.vehicleId,
      changes: {
        // Map markers gate their DOM updates on `hash`; the position change
        // must come with a new one or the marker never moves.
        hash: `set-location-${action.point.coordinates[1]}-${action.point.coordinates[0]}`,
        currentLocation: action.locationId
          ? {
              ...vehicle.currentLocation,
              id: action.locationId,
            }
          : null,
        lastState: {
          ...vehicle.lastState,
          type: VehicleStateType.stopped,
          duration: {
            lower: vehicle.lastState?.duration?.lower
              ? vehicle.lastState.duration.lower
              : new Date().toISOString(),
            upper: vehicle.lastState?.duration?.upper
              ? vehicle.lastState.duration.upper
              : new Date().toISOString(),
          },
        },
        lastReading: {
          ...vehicle.lastReading,
          heading: vehicle.lastReading?.heading || 0,
          signalStrength: vehicle.lastReading?.signalStrength || 0,
          batteryPercent: vehicle.lastReading?.batteryPercent ?? null,
          speed: vehicle.lastReading?.speed || '0',
          datetime: vehicle.lastReading?.datetime
            ? vehicle.lastReading.datetime
            : new Date(),
          point: {
            lat: action.point.coordinates[1],
            lng: action.point.coordinates[0],
          } as any,
        },
      },
    };
    return {
      ...adapter.updateOne(update, state),
      setLocationLoading: false,
    };
  }),
  on(CoreVehicleActions.setVehicleLocation, (state) => ({
    ...state,
    setLocationLoading: true,
  })),
  on(VehicleActions.addSpecToVehicleSuccess, (state, action) => ({
    ...adapter.updateOne(
      {
        id: action.vehicle.id,
        changes: { specifications: action.vehicle.specifications },
      },
      state,
    ),
    loading: false,
  })),
);

export const { selectIds, selectEntities, selectAll, selectTotal } =
  adapter.getSelectors();

export const getLoadedFields = (state: State) => state.fields;
export const getLoadedState = (state: State) => state.loaded;
export const getLoadingState = (state: State) => state.loading;
export const getCount = (state: State) => state.count;
export const getSelectedVehicleId = (state: State) => state.selectedVehicleId;
export const getUsedVehicleTypes = (state: State) => state.usedVehicleTypes;
export const getVehicleTypes = (state: State) => state.vehicleTypes;
export const getVehicleError = (state: State) => state.vehicleError;
export const getSetLocationLoading = (state: State) => state.setLocationLoading;
